import { useCallback } from 'react'
import { PublicLayout } from '@/components/PublicLayout'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, MessageSquareText, Wand2, Rocket } from 'lucide-react'
import { Link } from 'react-router-dom'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE_STANDARD: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94]

// ---------------------------------------------------------------------------
// Pipeline step data
// ---------------------------------------------------------------------------

const pipelineSteps = [
  {
    icon: MessageSquareText,
    title: 'Describe',
    description:
      'Tell us what you need in plain language. Requirements, workflows, business rules - no technical jargon required.',
  },
  {
    icon: Wand2,
    title: 'Generate',
    description:
      'AI scaffolds the application from a battle-tested core. Layouts, logic, integrations - assembled in hours, not weeks.',
  },
  {
    icon: Rocket,
    title: 'Deploy',
    description:
      'A certified developer reviews, hardens, and ships. You get production-ready software with a human seal of approval.',
  },
]

// ---------------------------------------------------------------------------
// Use case tags
// ---------------------------------------------------------------------------

const useCases = [
  'Booking Systems',
  'Inventory Management',
  'Delivery Tracking',
  'Appointment Scheduling',
  'Invoice & Billing',
  'Customer Portals',
  'Staff Dashboards',
  'Feedback Forms',
  'Loyalty Programs',
  'Order Management',
  'Fleet Tracking',
  'E-Commerce Storefronts',
]

// ---------------------------------------------------------------------------
// Shared animation helpers
// ---------------------------------------------------------------------------

function useFadeUp(prefersReducedMotion: boolean | null) {
  return useCallback(
    (delay: number, yStart = 40) => ({
      initial: prefersReducedMotion ? false : ({ opacity: 0, y: yStart } as const),
      whileInView: { opacity: 1, y: 0 },
      viewport: { once: true, amount: 0.15 } as const,
      transition: { duration: 0.65, ease: EASE_STANDARD, delay },
    }),
    [prefersReducedMotion]
  )
}

// ---------------------------------------------------------------------------
// Hero Section
// ---------------------------------------------------------------------------

function HeroSection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  return (
    <section className="relative overflow-hidden px-8 pb-16 pt-20 md:pt-28">
      {/* Gradient mesh */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 20% 40%, rgba(232,152,90,0.06) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 75% 35%, rgba(91,146,121,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-[1120px] grid-cols-1 items-center gap-12 lg:grid-cols-[1fr_1fr]">
        {/* Left: Text */}
        <div>
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
            {...fadeUp(0)}
          >
            Agentic Framework
          </motion.div>

          <motion.h1
            className="mb-6 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 'clamp(40px, 5vw, 56px)',
              fontWeight: 700,
              lineHeight: 1.08,
              letterSpacing: '-1.5px',
            }}
            {...fadeUp(0.1)}
          >
            Agentic
            <br />
            <span className="bg-gradient-to-br from-accent to-[#D4844E] bg-clip-text text-transparent">
              Frameworks
            </span>
          </motion.h1>

          <motion.p
            className="mb-9 max-w-[460px] text-lg leading-[1.65] text-muted-foreground"
            {...fadeUp(0.2, 30)}
          >
            Common solutions - booking systems, portals, dashboards - delivered fast through
            AI-assisted development and certified human oversight.
          </motion.p>

          <motion.div {...fadeUp(0.3, 24)}>
            <motion.div
              whileHover={
                prefersReducedMotion
                  ? {}
                  : {
                      y: -2,
                      scale: 1.02,
                      boxShadow: '0 8px 32px rgba(232,152,90,0.35)',
                    }
              }
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="inline-flex items-center gap-2.5 rounded-full bg-accent px-10 py-4 text-base font-bold text-accent-foreground shadow-[0_4px_24px_rgba(232,152,90,0.3)] transition-colors"
            >
              <Link
                to="/post"
                className='flex flex-row gap-2'
              >
                Get Started
                <ArrowRight className="h-[18px] w-[18px] transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Right: Illustration */}
        <motion.div
          className="flex items-center justify-center rounded-2xl border border-border bg-background p-6"
          initial={prefersReducedMotion ? false : { opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.75, ease: EASE_STANDARD, delay: 0.15 }}
        >
          <img
            src="/illustrations/ai.svg"
            alt="AI-powered software development"
            className="h-auto w-full max-w-[440px]"
            loading="eager"
          />
        </motion.div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// The Shift Section — typography as the visual anchor
// ---------------------------------------------------------------------------

function ShiftSection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  return (
    <section className="bg-card px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        <div className="mx-auto max-w-[680px] text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
            {...fadeUp(0)}
          >
            The Shift
          </motion.div>

          <motion.h2
            className="mb-6 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: '-0.5px',
            }}
            {...fadeUp(0.1)}
          >
            Why Wait Months for
            <br />
            What Exists Already?
          </motion.h2>

          <motion.p
            className="mx-auto mb-14 max-w-[520px] text-[17px] leading-[1.65] text-muted-foreground"
            {...fadeUp(0.2)}
          >
            Most small business software is 80% identical. The same auth flows, the same dashboards,
            the same CRUD operations. Traditional development rebuilds all of it from scratch, every
            single time.
          </motion.p>

          {/* Typography as illustration — the anchor stat */}
          <motion.div
            className="mx-auto max-w-[500px] rounded-[20px] border border-border bg-background px-10 py-12"
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.3 }}
          >
            <div
              className="mb-3 bg-gradient-to-br from-accent to-[#D4844E] bg-clip-text text-transparent"
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 'clamp(64px, 8vw, 88px)',
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: '-2px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              72 hrs
            </div>
            <p className="text-[17px] leading-[1.5] text-muted-foreground">
              Average time from description to deployed prototype.
              <br />
              <span className="font-semibold text-foreground">
                Traditional timeline: 6-12 weeks.
              </span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// How It Works Section — pipeline cards + single illustration
// ---------------------------------------------------------------------------

function HowItWorksSection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: EASE_STANDARD, delay: i * 0.12 },
    }),
  }

  return (
    <section className="relative bg-background px-8 py-[120px]">
      {/* Subtle gradient */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 50% 20%, rgba(232,152,90,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-[1120px]">
        {/* Header */}
        <div className="mb-16 text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
            {...fadeUp(0)}
          >
            How It Works
          </motion.div>

          <motion.h2
            className="mb-3 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
            {...fadeUp(0.1)}
          >
            The Pipeline
          </motion.h2>

          <motion.p
            className="mx-auto max-w-[480px] text-[17px] text-muted-foreground"
            {...fadeUp(0.2)}
          >
            Three stages. No ambiguity. From your words to working software.
          </motion.p>
        </div>

        {/* Pipeline cards — horizontal on desktop */}
        <div className="mb-16 grid grid-cols-1 gap-5 md:grid-cols-3">
          {pipelineSteps.map((step, i) => (
            <motion.div
              key={step.title}
              className="group relative rounded-[20px] border border-border bg-card p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-accent hover:shadow-[0_12px_32px_rgba(45,42,38,0.08)]"
              variants={cardVariants}
              custom={i}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              {/* Step number */}
              <div
                className="absolute -top-3 right-6 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground shadow-[0_2px_8px_rgba(232,152,90,0.25)]"
                style={{ fontFamily: 'Fraunces, Georgia, serif' }}
              >
                {i + 1}
              </div>

              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[14px] bg-accent/[0.08] transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110">
                <step.icon className="h-6 w-6 text-accent" />
              </div>

              <h3
                className="mb-2 text-lg font-bold text-foreground"
                style={{ fontFamily: 'Fraunces, Georgia, serif' }}
              >
                {step.title}
              </h3>
              <p className="text-sm leading-[1.65] text-muted-foreground">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Single illustration below the cards */}
        <motion.div
          className="mx-auto max-w-[520px] rounded-2xl border border-border bg-card p-6"
          initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE_STANDARD, delay: 0.1 }}
        >
          <img
            src="/illustrations/creation-process.svg"
            alt="Software materializing from the creation pipeline"
            className="mx-auto h-auto w-full max-w-[440px]"
            loading="lazy"
          />
        </motion.div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Use Cases Section — minimal pill grid, no illustration
// ---------------------------------------------------------------------------

function UseCasesSection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  const pillVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: EASE_STANDARD, delay: i * 0.06 },
    }),
  }

  return (
    <section className="bg-card px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        <div className="mb-14 text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
            {...fadeUp(0)}
          >
            Use Cases
          </motion.div>

          <motion.h2
            className="mb-3 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
            {...fadeUp(0.1)}
          >
            What Can You Build?
          </motion.h2>

          <motion.p
            className="mx-auto max-w-[480px] text-[17px] text-muted-foreground"
            {...fadeUp(0.2)}
          >
            If it's a common business tool, we've probably built it before. The agentic framework
            handles the patterns. You just describe the need.
          </motion.p>
        </div>

        {/* Pill grid */}
        <div className="mx-auto flex max-w-[800px] flex-wrap justify-center gap-3">
          {useCases.map((useCase, i) => (
            <motion.span
              key={useCase}
              className="cursor-default rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-[border-color,box-shadow] duration-200 hover:border-accent hover:shadow-[0_4px_12px_rgba(232,152,90,0.1)]"
              variants={pillVariants}
              custom={i}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {useCase}
            </motion.span>
          ))}
        </div>

        {/* Subtle footnote */}
        <motion.p className="mt-8 text-center text-[13px] text-muted-foreground" {...fadeUp(0.3)}>
          Don't see yours? Custom projects work too,{' '}
          <Link to="/post" className="font-medium text-accent underline-offset-2 hover:underline">
            just describe it
          </Link>
          .
        </motion.p>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Final CTA Section — clean, no illustration
// ---------------------------------------------------------------------------

function FinalCTASection() {
  const prefersReducedMotion = useReducedMotion()
  const fadeUp = useFadeUp(prefersReducedMotion)

  return (
    <section className="relative bg-background px-8 py-[140px] text-center">
      {/* Subtle gradient */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(232,152,90,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1120px]">
        <motion.h2
          className="mb-4 text-foreground"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 'clamp(36px, 4vw, 48px)',
            fontWeight: 700,
            letterSpacing: '-1px',
          }}
          {...fadeUp(0)}
        >
          Skip the
          <br />
          Boilerplate
        </motion.h2>

        <motion.p
          className="mx-auto mb-10 max-w-[440px] text-lg leading-relaxed text-muted-foreground"
          {...fadeUp(0.1)}
        >
          Describe what you need. Get production software in days, not months.
        </motion.p>

        <motion.div {...fadeUp(0.2)}>
          <motion.div
            className="inline-flex"
            whileHover={
              prefersReducedMotion
                ? {}
                : {
                    y: -2,
                    scale: 1.02,
                    boxShadow: '0 10px 40px rgba(232,152,90,0.35)',
                  }
            }
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link
              to="/post"
              className="inline-flex items-center gap-2.5 rounded-full bg-accent px-12 py-[18px] text-lg font-bold text-accent-foreground shadow-[0_6px_28px_rgba(232,152,90,0.3)] transition-colors"
            >
              Start a Project
              <ArrowRight className="h-5 w-5 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page Export
// ---------------------------------------------------------------------------

export default function AgenticPage() {
  return (
    <PublicLayout>
      <div className="bg-background text-foreground">
        <HeroSection />
        <ShiftSection />
        <HowItWorksSection />
        <UseCasesSection />
        <FinalCTASection />
      </div>
    </PublicLayout>
  )
}
